import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    List,
    ListItem,
    ListItemText,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Typography,
    Select,
    MenuItem,
    InputLabel,
    Box,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    FormControl,
    Grid,
    DialogActions,
    Divider,
    Switch,
} from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import { SwapHoriz } from '@material-ui/icons';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CircleIcon from '@mui/icons-material/Circle';
import SelectAllIcon from '@material-ui/icons/SelectAll';
import { v4 as uuidv4 } from 'uuid';
import SyncIcon from '@material-ui/icons/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import FlipIcon from '@material-ui/icons/Flip';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import AddIcon from '@material-ui/icons/AddCircle';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import LockIcon from '@material-ui/icons/Lock';

export default function ColumnEditor(props) {
    const state = useSelector(state => state);
    const allColumns = Array.isArray(props.columnList) ? props.columnList : (Array.isArray(props.columns) ? props.columns : []);
    const [columnInfoOpen, setColumnInfoOpen] = React.useState(false);
    const [mastSettingsOpen, setMastSettingsOpen] = React.useState(false);
    const [currColumn, setCurrColumn] = React.useState([]);
    const [currIndex, setCurrIndex] = React.useState(0);
    const [luminNameList, setLuminNameList] = React.useState([]);
    const [luminIdList, setLuminIdList] = React.useState([]);
    const [luminColorList, setLuminColorList] = React.useState([]);
    const [displayX, setDisplayX] = React.useState(0);
    const [displayY, setDisplayY] = React.useState(0);
    const [displayAngle, setDisplayAngle] = React.useState(0);
    const [columnColors, setColumnColors] = React.useState({});
    const [groupDialogOpen, setGroupDialogOpen] = React.useState(false);
    const [selectedColumns, setSelectedColumns] = React.useState([]);
    const [newGroupName, setNewGroupName] = React.useState("");
    const [targetGroupId, setTargetGroupId] = React.useState(0);
    const [groupSettingsOpen, setGroupSettingsOpen] = React.useState(false);
    const [groupName, setGroupName] = React.useState("");
    const [setLanternsDialogOpen, toggleSetLanternsDialog] = React.useState(false);
    const [groupId, setGroupId] = React.useState();
    const [groupHasColumns, setGroupHasColumns] = React.useState(false);
    const [groupSettingOptions, setGroupSettingOptions] = React.useState({
        height: false,
        outreach: false,
        tilt: false,
        cant: false,
        luminaireId: false,
        positionAngle: false,
    });
    const isGroupLocked = (groupId) => Boolean(props.columnGroups.find(group => String(group.id) === String(groupId))?.locked);
    const normalizeAngle360 = (angle) => {
        const numeric = Number(angle);
        if (!Number.isFinite(numeric)) return 0;
        return ((numeric % 360) + 360) % 360;
    };
    // Stored angle currently follows the opposite convention; show clockwise-friendly UI angle.
    const storedToUiAngle = (storedAngle) => Math.round((normalizeAngle360(360 - normalizeAngle360(storedAngle))) * 100) / 100;
    const uiToStoredAngle = (uiAngle) => normalizeAngle360(360 - normalizeAngle360(uiAngle));

    const [expandedGroups, setExpandedGroups] = React.useState({ 0: true });
    const handleToggleGroupExpanded = (groupId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const handleInfoOpen = (index) => {
        const column = props.columns.find(col => col.id === index);

        setDisplayX((column.values.point.x / props.scale).toFixed(2));
        setDisplayY((column.values.point.y / props.scale).toFixed(2));
        const rawAngle = Number(column.values.positionAngle ?? 0);
        const normalized = storedToUiAngle(rawAngle);
        setDisplayAngle(normalized.toFixed(2));
        setCurrColumn(column);
        setCurrIndex(index);

        if (column.isMast) {
            setMastSettingsOpen(true);
        } else {
            setColumnInfoOpen(true);
        }
    };

    const handleLanternOutreach = (e) => {
        setCurrColumn((prevColumn) => ({
            ...prevColumn,
            values: {
                ...prevColumn.values,
                useLanternOutreach: e.target.checked,
            }
        }));
    };

    useEffect(() => {
        if (
            currColumn?.values?.lanterns &&
            currColumn?.values?.configuration !== "None"
        ) {
            const newTilt = currColumn.values.tilt || 0;
            const mountingType = currColumn.values.mountingType;
            const positionAngle = currColumn.values.positionAngle;
            const lanterns = currColumn.values.lanterns;
            const count = lanterns.length;

            const updatedLanterns = lanterns.map((lantern, index) => {
                let positionAngleUpdate =
                    mountingType === "wall"
                        ? positionAngle
                        : uiToStoredAngle((360 / count) * index);

                let beamEndPoint = lantern.beamAimingInfo?.beamEndPoint;

                if (mountingType === "wall") {
                    beamEndPoint = undefined;
                }

                return {
                    ...lantern,
                    positionAngle: positionAngleUpdate,
                    beamAimingInfo: {
                        ...lantern.beamAimingInfo,
                        tilt: newTilt,
                        beamEndPoint,
                    },
                };
            });

            setCurrColumn((prevColumn) => ({
                ...prevColumn,
                values: {
                    ...prevColumn.values,
                    lanterns: updatedLanterns,
                },
            }));
        }
    }, [
        currColumn?.values?.mountingType,
        currColumn?.values?.configuration,
        currColumn?.values?.positionAngle,
        currColumn?.values?.tilt,
    ]);

    const handleChangeCurrColumn = (e) => {
        const { name, value } = e.target;

        if (name === 'name') {
            setCurrColumn((prevColumn) => ({
                ...prevColumn,
                name: value
            }));
        } else if (name === 'x' || name === 'y') {
            if (name === 'x') setDisplayX(value);
            if (name === 'y') setDisplayY(value);
        } else if (name === 'angle' || name === 'positionAngle') {
            setDisplayAngle(value);
            if (value === '' || value === null || value === undefined) return;
            const storedAngle = uiToStoredAngle(value);
            setCurrColumn((prevColumn) => ({
                ...prevColumn,
                values: {
                    ...prevColumn.values,
                    positionAngle: storedAngle,
                }
            }));
        }
        else if (name === 'luminaireId') {
            const newColumn = {
                ...currColumn,
                values: {
                    ...currColumn.values,
                    luminaireId: value
                }
            };

            const luminaireIndex = luminIdList.indexOf(value);
            const newColor = luminaireIndex >= 0 ? luminColorList[luminaireIndex] : '#FFA500';

            setCurrColumn(newColumn);
            setColumnColors(prevColors => ({
                ...prevColors,
                [currColumn.id]: newColor
            }));
        } else if (name === 'mounting') {
            setCurrColumn((prevColumn) => {
                const updatedLanterns = prevColumn.values.lanterns.map(lantern => ({
                    ...lantern,
                    offset: parseFloat(lantern.offset || 0)
                }));

                return {
                    ...prevColumn,
                    values: {
                        ...prevColumn.values,
                        mountingType: value,
                        lanterns: updatedLanterns,
                    }
                };
            });
        } else {
            setCurrColumn((prevColumn) => ({
                ...prevColumn,
                values: {
                    ...prevColumn.values,
                    [name]: value
                }
            }));
        }
    };


    const swapColumnAndMast = (name, isMast) => {
        console.log(props)

        if (name.includes("Column") && isMast !== true) {
            return name.replace("Column", "Mast");
        } else if (name.includes("Mast") && isMast === true) {
            return name.replace("Mast", "Column");
        }
        return name;
    };

    const getTiltFromBeamLength = (beamLength, height = 6) => {
        const radians = Math.atan(beamLength / height);
        const degrees = radians * (180 / Math.PI);
        return +(degrees).toFixed(2);
    };


    const swapType = (id) => {
        console.log("Swapping type for column ID:", id, props.columns.find(column => column.id === id));
        const updatedColumns = props.columns.map(column =>
            column.id === id
                ? {
                    ...column,
                    isMast: !column.isMast,
                    values: {
                        ...column.values,
                        mountingType: column.values.mountingType || "column",
                        useLanternOutreach: column.values.useLanternOutreach || false,
                        lanterns: column.isMast ? column.values.lanterns : column.values.lanterns || [
                            {
                                positionAngle: 0,
                                offset: 0,
                                outreach: column.values.outreach,
                                luminaireId: column.values.luminaireId,
                                color: columnColors[column.id],
                                beamAimingInfo: {
                                    beamLength: column.values.beamLength || 0.75,
                                    beamEndPoint: {
                                        x: column.values.point.x + (column.values.beamLength || 0.75) * Math.cos((0 - 90) * Math.PI / 180),
                                        y: column.values.point.y + (column.values.beamLength || 0.75) * Math.sin((0 - 90) * Math.PI / 180),
                                    },
                                    beamOrigin: {
                                        x: column.values.point.x,
                                        y: column.values.point.y,
                                    },
                                    tilt: getTiltFromBeamLength(column.values.beamLength, column.values.height) || column.values.tilt || 0,
                                }
                            }
                        ]
                    },
                    name: swapColumnAndMast(column.name, column.isMast),
                }
                : column
        );
        props.setColumns(updatedColumns);
    };

    const handleSave = () => {

        const rawUiAngle = parseFloat(displayAngle ?? storedToUiAngle(currColumn.values.positionAngle ?? 0));
        const storedAngle = uiToStoredAngle(rawUiAngle);
        const updatedItem = {
            ...currColumn,
            values: {
                ...currColumn.values,
                positionAngle: storedAngle,
                point: {
                    x: displayX * props.scale,
                    y: displayY * props.scale
                }
            }
        };

        const updatedColumns = props.columnList.map(col =>
            col.id === currColumn.id ? updatedItem : col
        );

        props.setColumns(updatedColumns);
        setColumnInfoOpen(false);
    };


    const handleAddLantern = (count) => {
        const lanterns = currColumn?.values?.lanterns || [];

        const baseAngles = Array.from({ length: 8 }, (_, i) => i * 22.5);
        const preferredAngles = [];

        for (let i = 0; i < baseAngles.length; i++) {
            const angle = baseAngles[i];
            preferredAngles.push(angle);
            preferredAngles.push((angle + 180) % 360);
        }

        const generateLantern = (lanternsSoFar, overrideAngle = null) => {
            const mountingType = currColumn?.values?.mountingType;
            const existingAngles = lanternsSoFar.map(l => Number(l.positionAngle));
            let defaultAngle;

            if (mountingType === "wall") {
                defaultAngle = Number(currColumn?.values?.positionAngle ?? 0);
            } else {
                if (overrideAngle !== null && !isNaN(overrideAngle)) {
                    defaultAngle = Number(overrideAngle);
                } else {
                    const foundAngle = preferredAngles.find(a => !existingAngles.includes(a));
                    defaultAngle = typeof foundAngle === 'number'
                        ? foundAngle
                        : (lanternsSoFar.length * 23) % 360;
                }
            }

            const existingOffsets = lanternsSoFar.map(l => Number(l.offset));
            let offset = 0;
            if (existingOffsets.includes(0)) {
                let absOffset = 1;
                while (existingOffsets.includes(-absOffset) && existingOffsets.includes(absOffset)) {
                    absOffset++;
                }
                offset = !existingOffsets.includes(-absOffset) ? -absOffset : absOffset;
            }

            const defaultLuminaireId = luminIdList[0];
            const color = luminColorList[luminIdList.indexOf(defaultLuminaireId)];
            const beamLength = parseFloat(currColumn?.values?.beamLength ?? 0.75);

            console.log("new column", currColumn);

            const displayX = Number(currColumn?.values?.point?.x || 0);
            const displayY = Number(currColumn?.values?.point?.y || 0);

            const columnAngleDeg = Number(currColumn?.values?.positionAngle ?? 0);
            const columnAngleRad = (columnAngleDeg * Math.PI) / 180;

            const armX = Number(offset);
            const armY = -0.2;

            const preRotatedBase = {
                x: displayX + armX,
                y: displayY + armY,
            };

            const rotateAround = (point, center, angleRad) => {
                const translated = {
                    x: point.x - center.x,
                    y: point.y - center.y,
                };
                const rotated = {
                    x: translated.x * Math.cos(angleRad) - translated.y * Math.sin(angleRad),
                    y: translated.x * Math.sin(angleRad) + translated.y * Math.cos(angleRad),
                };
                return {
                    x: center.x + rotated.x,
                    y: center.y + rotated.y,
                };
            };

            const beamOrigin = rotateAround(preRotatedBase, { x: displayX, y: displayY }, columnAngleRad);

            return {
                id: uuidv4(),
                positionAngle: uiToStoredAngle(defaultAngle),
                offset: Number(offset),
                luminaireId: defaultLuminaireId,
                color,
                outreach: Number(currColumn?.values?.outreach || 0),
                beamAimingInfo: {
                    beamLength: Number(beamLength),
                    beamOrigin,
                    tilt: Number(getTiltFromBeamLength(beamLength, currColumn.values?.height)),
                }
            };
        };

        let newLanterns = [...lanterns];

        if (typeof count === 'number') {
            newLanterns = [];
            for (let i = 0; i < Math.min(count, 16); i++) {
                const evenlySpacedAngle = (360 / count) * i;
                newLanterns.push(generateLantern(newLanterns, evenlySpacedAngle));
            }
        } else {
            if (newLanterns.length < 16) {
                newLanterns.push(generateLantern(newLanterns));
            }
        }

        handleChangeCurrColumn({
            target: {
                name: 'lanterns',
                value: newLanterns
            }
        });
    };

    const handleRemoveLantern = (index) => {
        const updatedLanterns = [...currColumn?.values?.lanterns];
        updatedLanterns.splice(index, 1);

        handleChangeCurrColumn({
            target: {
                name: 'lanterns',
                value: updatedLanterns
            }
        });
    };

    const handleLanternAngleChange = (index, angle) => {
        const updatedLanterns = [...currColumn?.values?.lanterns];
        updatedLanterns[index].positionAngle = uiToStoredAngle(angle);
        handleChangeCurrColumn({ target: { name: 'lanterns', value: updatedLanterns } });
    };

    const handleLanternOffsetChange = (index, offset) => {
        const updatedLanterns = [...currColumn?.values?.lanterns];
        updatedLanterns[index].offset = offset;
        handleChangeCurrColumn({ target: { name: 'lanterns', value: updatedLanterns } });
    };

    const handleLanternLuminaireChange = (index, luminaireId) => {
        const lanterns = [...(currColumn?.values?.lanterns || [])];
        const color = luminColorList[luminIdList.indexOf(luminaireId)];

        lanterns[index] = {
            ...lanterns[index],
            luminaireId,
            color
        };

        handleChangeCurrColumn({ target: { name: "lanterns", value: lanterns } });
    };

    const handleLanternOutreachChange = (index, outreach) => {
        const lanterns = [...(currColumn?.values?.lanterns || [])];

        lanterns[index] = {
            ...lanterns[index],
            outreach: outreach
        };

        handleChangeCurrColumn({ target: { name: "lanterns", value: lanterns } });
    }

    const handleLanternTiltChange = (index, tilt) => {
        const lanterns = [...(currColumn?.values?.lanterns || [])];

        lanterns[index] = {
            ...lanterns[index],
            beamAimingInfo: {
                ...lanterns[index].beamAimingInfo,
                tilt: tilt
            }
        };

        handleChangeCurrColumn({ target: { name: "lanterns", value: lanterns } });
    }

    const getBeamLengthFromTilt = (tilt, height = 6) => {
        const radians = (90 - tilt) * (Math.PI / 180);
        const beamLength = height / Math.tan(radians);
        return +beamLength.toFixed(3);
    };


    const handleSaveMasts = () => {
        let lanterns = [...(currColumn?.values?.lanterns || [])];
        const mountingType = currColumn?.values?.mountingType;
        const rawUiAngle = parseFloat(displayAngle ?? storedToUiAngle(currColumn?.values?.positionAngle ?? 0));
        const storedAngle = uiToStoredAngle(rawUiAngle);

        if (mountingType === 'column') {
            // === Column mounting type logic ===
            const height = currColumn?.values?.height || 6;
            const defaultOutreach = currColumn?.values?.outreach || 0;
            const numericDisplayX = Number(displayX);
            const numericDisplayY = Number(displayY);

            lanterns = lanterns.map((lantern) => {
                const positionAngle = normalizeAngle360(parseFloat(lantern.positionAngle ?? 0));
                const offset = parseFloat(lantern.offset ?? 0);
                const tilt = parseFloat(lantern.beamAimingInfo?.tilt ?? 0);
                const outreach = parseFloat(lantern.outreach ?? defaultOutreach);

                // Trust positionAngle + tilt; recompute beamEndPoint
                let beamLength = getBeamLengthFromTilt(tilt, height);
                beamLength = Math.max(beamLength, 0.75);

                const angleRad = (Number(positionAngle) - 90) * Math.PI / 180;

                return {
                    ...lantern,
                    positionAngle: Number(positionAngle.toFixed(2)),
                    offset,
                    outreach,
                    beamAimingInfo: {
                        beamLength,
                        beamEndPoint: {
                            x: numericDisplayX + beamLength * Math.cos(angleRad),
                            y: numericDisplayY + beamLength * Math.sin(angleRad),
                        },
                        tilt
                    }
                };
            });
        } else {
            // === Wall mounting type logic ===
            const barThickness = 0.4;

            const columnAngleDeg = Number(currColumn?.values?.positionAngle ?? 0);
            const columnAngleRad = (columnAngleDeg * Math.PI) / 180;

            const barCenter = { x: Number(displayX), y: Number(displayY) };

            const offsets = lanterns.map(l => parseFloat(l.offset) || 0);
            const minOffset = Math.min(...offsets);
            const maxOffset = Math.max(...offsets);
            const centerOffset = (minOffset + maxOffset) / 2;

            const rotateAround = (point, center, angleRad) => {
                const translated = {
                    x: point.x - center.x,
                    y: point.y - center.y,
                };
                const rotated = {
                    x: translated.x * Math.cos(angleRad) - translated.y * Math.sin(angleRad),
                    y: translated.x * Math.sin(angleRad) + translated.y * Math.cos(angleRad),
                };
                return {
                    x: center.x + rotated.x,
                    y: center.y + rotated.y,
                };
            };

            lanterns = lanterns.map((lantern) => {
                const offset = parseFloat(lantern.offset ?? 0);
                const outreach = parseFloat(lantern.outreach ?? currColumn.values.outreach ?? 0);
                const columnHeight = currColumn.values.height || 6;

                const offsetFromCenter = offset - centerOffset;
                const armX = offsetFromCenter;
                const armY = -barThickness / 2;

                const preRotatedBase = {
                    x: barCenter.x + armX,
                    y: barCenter.y + armY,
                };

                const beamOrigin = rotateAround(preRotatedBase, barCenter, columnAngleRad);

                let beamEndPoint = lantern.beamAimingInfo?.beamEndPoint;
                let beamLength;
                let tilt;
                let positionAngle;

                const isBeamValid = (
                    beamEndPoint &&
                    typeof beamEndPoint.x === "number" && !isNaN(beamEndPoint.x) &&
                    typeof beamEndPoint.y === "number" && !isNaN(beamEndPoint.y)
                );

                if (isBeamValid) {
                    const dx = beamEndPoint.x - beamOrigin.x;
                    const dy = beamEndPoint.y - beamOrigin.y;

                    beamLength = Math.sqrt(dx * dx + dy * dy);
                    tilt = getTiltFromBeamLength(beamLength, columnHeight);

                    const angleFromVertical = Math.atan2(dy, dx) * 180 / Math.PI;
                    positionAngle = normalizeAngle360(angleFromVertical + 90);
                } else {
                    tilt = parseFloat(lantern.beamAimingInfo?.tilt ?? 0);
                    beamLength = getBeamLengthFromTilt(tilt, columnHeight);
                    beamLength = Math.max(beamLength, 0.75);

                    positionAngle = normalizeAngle360(parseFloat(lantern.positionAngle ?? 0));
                    positionAngle = Number(positionAngle.toFixed(2));

                    const beamAngleRad = (positionAngle - 90) * Math.PI / 180;

                    beamEndPoint = {
                        x: beamOrigin.x + beamLength * Math.cos(beamAngleRad),
                        y: beamOrigin.y + beamLength * Math.sin(beamAngleRad),
                    };
                }

                return {
                    ...lantern,
                    positionAngle: Number(positionAngle.toFixed(2)),
                    offset,
                    outreach,
                    beamAimingInfo: {
                        ...lantern.beamAimingInfo,
                        beamLength,
                        beamOrigin,
                        beamEndPoint,
                        tilt,
                    },
                };
            });
        }

        const updatedItem = {
            ...currColumn,
            values: {
                ...currColumn.values,
                positionAngle: storedAngle,
                lanterns,
                point: {
                    x: displayX * props.scale,
                    y: displayY * props.scale
                }
            }
        };

        const updatedMasts = props.columnList.map(mast =>
            mast.id === currColumn.id ? updatedItem : mast
        );

        props.setColumns(updatedMasts);
        setMastSettingsOpen(false);
    };


    useEffect(() => {
        const wasOpen = previousOpenRef.current;

        if (wasOpen && !mastSettingsOpen) {
            props.doAreaCalc();
        }

        previousOpenRef.current = mastSettingsOpen;
    }, [mastSettingsOpen]);

    const previousOpenRef = React.useRef(mastSettingsOpen);

    const handleCenterMarkOnCurrentColumn = () => {
        if (typeof props.setMarkFromColumnCoords !== "function") return;

        const parsedX = Number(displayX);
        const parsedY = Number(displayY);

        const fallbackX = Number(currColumn?.values?.point?.x);
        const fallbackY = Number(currColumn?.values?.point?.y);

        const worldX = Number.isFinite(parsedX) ? parsedX * props.scale : fallbackX;
        const worldY = Number.isFinite(parsedY) ? parsedY * props.scale : fallbackY;

        if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) return;

        props.setMarkFromColumnCoords({ x: worldX, y: worldY });
    };


    const handleGroupDialogOpen = () => {
        setGroupDialogOpen(true);
    };

    const handleGroupDialogClose = () => {
        setGroupDialogOpen(false);
        setSelectedColumns([]);
        setNewGroupName("");
    };

    const handleColumnSelection = (columnId) => {
        setSelectedColumns((prevSelected) =>
            prevSelected.includes(columnId)
                ? prevSelected.filter((id) => id !== columnId)
                : [...prevSelected, columnId]
        );
    };

    const handleEditGroupSave = () => {
        if (newGroupName !== "") {
            console.log("Creating new group with name:", newGroupName);
            // Remove selected columns from other groups
            const updatedGroups = props.columnGroups.map(group => ({
                ...group,
                columnIds: group.columnIds.filter(id => !selectedColumns.includes(id))
            }));
            const newGroup = {
                id: uuidv4(),
                name: newGroupName,
                columnIds: selectedColumns,
                sync: false,
                locked: false,
            }
            console.log("New group created:", newGroup);
            props.setColumnGroups([...updatedGroups, newGroup]);
        } else if (targetGroupId !== -1) {
            const updatedColumnGroups = props.columnGroups.map(group => {
                if (group.id === targetGroupId) {
                    return {
                        ...group,
                        columnIds: [...new Set([...group.columnIds, ...selectedColumns])]   // Ensure unique column IDs
                    };
                } else {
                    // remove selected columns from other groups
                    group.columnIds = group.columnIds.filter(id => !selectedColumns.includes(id));
                }
                return group;
            });
            props.setColumnGroups(updatedColumnGroups);
        }
        setTargetGroupId(0);
        setNewGroupName("");
        handleGroupDialogClose();
    };

    const handleGroupSettings = (groupId) => {
        const group = props.columnGroups.find(g => String(g.id) === String(groupId));
        const columnsIdInGroup = group ? group.columnIds : [];
        const groupColumns = allColumns.filter(column => columnsIdInGroup.includes(column.id));
        // Create a new column object for the group, using the first column as a template but with a new id and name
        const tempCol = {
            id: 0,
            name: group.name + " (Group Column)",
            values: {
                positionAngle: 0,
                // columnConfiguration: "Single Sided Left",
                // dimFactor: 0,
                // columnGrid: 0,
                height: 0,
                maintenanceFactor: 0,
                outreach: 0,
                cant: 0,
                tilt: 0,
                luminaireId: null,
            },
        };
        setCurrColumn(tempCol);
        setGroupName(group?.name || "");
        setGroupId(groupId);
        setGroupHasColumns(groupColumns.length > 0);
        setGroupSettingsOpen(true);
    };

    const handleGroupSettingSave = () => {
        // Build an object with only the fields where groupSettingOptions[field] is true
        console.log('handleGroupSettingSave', currColumn, groupSettingOptions, groupId);
        const fieldsToUpdate = Object.keys(groupSettingOptions).reduce((acc, field) => {
            if (groupSettingOptions[field]) {
                acc[field] = currColumn.values[field];
            }
            return acc;
        }, {});

        const updatedColumns = allColumns.map(column =>
            props.columnGroups.find(g => String(g.id) === String(groupId))?.columnIds?.includes(column.id)
                ? { ...column, values: { ...column.values, ...fieldsToUpdate } }
                : column
        );
        const trimmedGroupName = (groupName || "").trim();
        const updatedGroups = props.columnGroups.map(group =>
            String(group.id) === String(groupId) && group.id !== 0 && trimmedGroupName !== ""
                ? { ...group, name: trimmedGroupName }
                : group
        );
        console.log("Updated fieldsToUpdate", fieldsToUpdate);
        console.log("Updated Columns", updatedColumns);
        if (groupHasColumns) {
            props.setColumns(updatedColumns);
        }
        props.setColumnGroups(updatedGroups);
        setGroupSettingsOpen(false);
    };

    const handleGroupSelect = (groupId) => {
        const group = props.columnGroups.find(g => String(g.id) === String(groupId));
        console.log("handleGroupSelect", group);
        const columnsIdInGroup = group ? group.columnIds : [];
        if (group?.locked) {
            return;
        }
        if (columnsIdInGroup.length !== 0) {
            const groupColumns = allColumns.filter(column => columnsIdInGroup.includes(column.id));

            // Check if all are selected
            const allSelected = groupColumns.every(column => column.selected);
            // Toggle selection: deselect all if all selected, else select all
            const updatedColumns = allColumns.map(column =>
                columnsIdInGroup.some(id => id === column.id)
                    ? { ...column, selected: !allSelected }
                    : column
            );
            props.setColumns(updatedColumns);
        }
    };

    const handleGroupSync = (groupId) => {
        const temp = props.columnGroups;
        temp.find(g => String(g.id) === String(groupId)).sync = !temp.find(g => String(g.id) === String(groupId)).sync;

        props.setColumnGroups([...temp]);
    }

    const handleGroupLock = (groupId) => {
        const updatedColumns = allColumns.map(column => {
            const inGroup = props.columnGroups.find(group => String(group.id) === String(groupId))?.columnIds?.includes(column.id);
            return inGroup ? { ...column, selected: false } : column;
        });
        const updatedGroups = props.columnGroups.map(group =>
            String(group.id) === String(groupId)
                ? { ...group, locked: !Boolean(group.locked) }
                : group
        );
        props.setColumnsUiOnly(updatedColumns);
        props.setColumnGroups(updatedGroups);
    };

    const handleGroupFlipHorizontally = (groupId) => {
        // Find the group
        const group = props.columnGroups.find(g => String(g.id) === String(groupId));
        if (!group) return;

        // Get columns in the group
        const groupColumns = allColumns.filter(col => group.columnIds.includes(col.id));
        if (groupColumns.length === 0) return;

        // Find the center X of the group
        const xs = groupColumns.map(col => col.values?.point?.x ?? 0);
        const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;

        // Flip each column horizontally around the center X
        const updatedColumns = allColumns.map(col => {
            if (group.columnIds.includes(col.id) && col.values?.point) {
                return {
                    ...col,
                    values: {
                        ...col.values,
                        point: {
                            ...col.values.point,
                            x: 2 * centerX - col.values.point.x
                        },
                    }
                };
            }
            return col;
        });

        props.setColumns(updatedColumns);
    }

    const handleGroupFlipVertically = (groupId) => {
        // Find the group
        const group = props.columnGroups.find(g => String(g.id) === String(groupId));
        if (!group) return;

        // Get columns in the group
        const groupColumns = allColumns.filter(col => group.columnIds.includes(col.id));
        if (groupColumns.length === 0) return;

        // Find the center Y of the group
        const ys = groupColumns.map(col => col.values?.point?.y ?? 0);
        const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;

        // Flip each column vertically around the center Y
        const updatedColumns = allColumns.map(col => {
            if (group.columnIds.includes(col.id) && col.values?.point) {
                return {
                    ...col,
                    values: {
                        ...col.values,
                        point: {
                            ...col.values.point,
                            y: 2 * centerY - col.values.point.y
                        },
                    }
                };
            }
            return col;
        });

        props.setColumns(updatedColumns);
    }

    const handleGroupDuplicate = (groupId) => {
        const group = props.columnGroups.find(g => String(g.id) === String(groupId));
        if (!group) return;

        // Duplicate columns in the group
        const groupColumns = allColumns.filter(col => group.columnIds.includes(col.id));
        const idMap = {};
        const duplicatedColumns = groupColumns.map(col => {
            const newId = uuidv4();
            idMap[col.id] = newId;
            return {
                ...col,
                id: newId,
                name: `${col.name} (Copy)`,
                selected: false,
                values: {
                    ...col.values,
                    point: {
                        x: col.values.point.x + 0.1, // Offset to avoid overlap
                        y: col.values.point.y + 0.1,
                    },
                }
            };
        });

        // Add duplicated columns to the main columns list
        props.setColumns([...allColumns, ...duplicatedColumns]);

        // Create a new group for the duplicated columns
        const newGroup = {
            id: uuidv4(),
            name: `${group.name} (Copy)`,
            columnIds: duplicatedColumns.map(col => col.id),
            sync: false,
            locked: false,
        };

        props.setColumnGroups([...props.columnGroups, newGroup]);
    };

    // const handleGroupDelete = (groupId) => {
    //     if (groupId === 0) {
    //         alert("Default Group cannot be deleted.");
    //         return;
    //     }
    //     const updatedColumnGroups = props.columnGroups.filter(group => group.id !== groupId);
    //     const group = props.columnGroups.find(g => String(g.id) === String(groupId));
    //     group.columnIds.map(id => {
    //         props.deleteColumn(id)
    //     });
    //     props.setColumnGroups(updatedColumnGroups);
    // };

    const handleUngroup = (groupId) => {
        if (groupId === 0) {
            alert("Default Group cannot be deleted.");
            return;
        }
        // Move all columns in the group to the default group (id = 0)
        const group = props.columnGroups.find(g => String(g.id) === String(groupId));
        if (!group) return;

        // Add the group's columns to the default group (id = 0), ensuring no duplicates
        const defaultGroup = props.columnGroups.find(g => g.id === 0);
        const updatedDefaultGroup = {
            ...defaultGroup,
            columnIds: Array.from(new Set([...(defaultGroup.columnIds || []), ...(group.columnIds || [])]))
        };

        // Remove the group to be deleted and update the default group
        const updatedColumnGroups = props.columnGroups
            .filter(g => g.id !== groupId)
            .map(g => (g.id === 0 ? updatedDefaultGroup : g));

        props.setColumnGroups(updatedColumnGroups);
    };

    const handleGroupSettingSelectAll = () => {
        setGroupSettingOptions(prev => {
            const allTrue = {};
            Object.keys(prev).forEach(key => {
                allTrue[key] = true;
            });
            return allTrue;
        });
    };

    const handleGroupSettingUnselectAll = () => {
        setGroupSettingOptions(prev => {
            const allFalse = {};
            Object.keys(prev).forEach(key => {
                allFalse[key] = false;
            });
            return allFalse;
        });
    };

    useEffect(() => {
        if (state.Plan?.selectedLuminaireNames) {
            setLuminNameList(state.Plan?.selectedLuminaireNames);
        }
        if (state.Plan?.selectedLuminaires) {
            setLuminIdList(state.Plan?.selectedLuminaires);
        }
        if (state.Plan?.selectedLuminaireColors) {
            setLuminColorList(state.Plan?.selectedLuminaireColors);
        }
    }, [state.Plan]);

    useEffect(() => {
        const updatedColors = {};
        props.columns.forEach(column => {
            const luminaireId = column.values?.luminaireId;
            const luminaireIndex = luminIdList.indexOf(luminaireId);
            if (luminaireIndex >= 0) {
                updatedColors[column.id] = luminColorList[luminaireIndex];
            } else {
                updatedColors[column.id] = '#FFA500 ';
            }
        });
        setColumnColors(updatedColors);
    }, [luminIdList, luminColorList, props.columns]);

    const editGroupDialog = () => {
        return (
            <Dialog
                open={groupDialogOpen}
                disableEscapeKeyDown
                disableEnforceFocus
                hideBackdrop
                BackdropProps={{ open: false }}
                onClose={handleGroupDialogClose}
            >
                <DialogTitle>Edit Groups</DialogTitle>
                <DialogContent>
                    <Typography>
                        Select columns to assign them to a group. You can also create a new group or select an existing group to assign the selected columns.
                    </Typography>
                    {(Array.isArray(props.columnGroups) ? props.columnGroups : []).map((group) => (
                        <Accordion key={group.name} >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">{group.name}</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                                {group.columnIds && props.columns?.filter(column => group.columnIds?.includes(column.id))?.map((column) => (
                                    <FormControlLabel
                                        key={column.id}
                                        control={
                                            <Checkbox
                                                checked={selectedColumns.includes(column.id)}
                                                onChange={() => handleColumnSelection(column.id)}
                                            />
                                        }
                                        label={column.name}
                                    />
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    <Typography style={{ marginTop: '10px' }}>
                        Enter a new group name or select an existing group:
                    </Typography>
                    <Select
                        value={targetGroupId}
                        onChange={(e) => setTargetGroupId(e.target.value)}
                        style={{ margin: '10px', width: '100%' }}
                        variant="outlined"
                    >
                        <MenuItem
                            key="New Group"
                            value={-1}
                            style={{ fontWeight: 'bold' }}
                        >
                            {"[Assign To New Group]"}
                        </MenuItem>
                        {props.columnGroups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <TextField
                        label="New Group Name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        style={{ margin: '10px', width: '100%' }}
                        variant="outlined"
                        hidden={targetGroupId !== -1}
                    />
                </DialogContent>
                <div style={{ textAlign: "right", margin: 10 }}>
                    <Button
                        onClick={handleEditGroupSave}
                        color="primary"
                        variant="contained"
                        style={{ margin: '10px' }}
                    >
                        Save
                    </Button>
                    <Button
                        onClick={handleGroupDialogClose}
                        color="primary"
                        variant="contained"
                        style={{ margin: '10px' }}
                    >
                        Cancel
                    </Button>
                </div>
            </Dialog>
        );
    };

    return (
        <>
            <Dialog
                open={columnInfoOpen}
                disableEscapeKeyDown
                disableEnforceFocus
                hideBackdrop
                BackdropProps={{ open: false }}
            >
                <DialogTitle>Column Setting</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ p: 1 }}>
                        {/* Column Name & Height */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Column Name"
                                value={currColumn?.name || ''}
                                onChange={handleChangeCurrColumn}
                                variant="outlined"
                                name="name"
                            />
                        </Grid>

                        {/* Position and Angle */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="x"
                                value={displayX || ''}
                                onChange={handleChangeCurrColumn}
                                variant="outlined"
                                type="number"
                                name="x"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="y"
                                value={displayY || ''}
                                onChange={handleChangeCurrColumn}
                                variant="outlined"
                                type="number"
                                name="y"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Rotation Angle (°)"
                                value={displayAngle ?? 0}
                                onChange={handleChangeCurrColumn}
                                variant="outlined"
                                type="number"
                                name="angle"
                            />
                        </Grid>

                        {/* Divider */}
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>

                        {/* Additional Properties */}
                        {['height', 'outreach'].map((field) => (
                            <Grid item xs={12} sm={6} key={field}>
                                <TextField
                                    fullWidth
                                    label={field.charAt(0).toUpperCase() + field.slice(1) + ' (m)'}
                                    value={currColumn?.values?.[field] ?? ''}
                                    onChange={handleChangeCurrColumn}
                                    variant="outlined"
                                    type="number"
                                    name={field}
                                />
                            </Grid>
                        ))}

                        {['tilt', 'cant'].map((field) => (
                            <Grid item xs={12} sm={6} key={field}>
                                <TextField
                                    fullWidth
                                    label={field.charAt(0).toUpperCase() + field.slice(1) + ' (°)'}
                                    value={currColumn?.values?.[field] ?? ''}
                                    onChange={handleChangeCurrColumn}
                                    variant="outlined"
                                    type="number"
                                    name={field}
                                />
                            </Grid>
                        ))}

                        {/* Luminaire Select */}
                        <Grid item xs={12}>
                            <InputLabel id="luminaireId">Column Type</InputLabel>
                            {luminNameList?.length > 0 && luminIdList.length > 0 ? (
                                <Select
                                    fullWidth
                                    value={currColumn?.values?.luminaireId || luminIdList[0]}
                                    onChange={handleChangeCurrColumn}
                                    variant="outlined"
                                    name="luminaireId"
                                    label="luminaireId"
                                    sx={{
                                        color: luminColorList[
                                            luminIdList.indexOf(currColumn?.values?.luminaireId || luminIdList[0])
                                        ],
                                    }}
                                >
                                    {luminNameList.map((option, id) => (
                                        <MenuItem
                                            key={option}
                                            value={luminIdList[id]}
                                            style={{ color: luminColorList[id] }}
                                        >
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            ) : (
                                <Typography>Please Add Column Type(s)</Typography>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCenterMarkOnCurrentColumn} variant="outlined">
                        Center Mark
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Save
                    </Button>
                    <Button onClick={() => setColumnInfoOpen(false)} variant="contained">
                        Cancel
                    </Button>
                </DialogActions>

            </Dialog>

            {/* Mast Settings */}
            <Dialog
                open={mastSettingsOpen}
                onClose={() => setMastSettingsOpen(false)}
                disableEscapeKeyDown
                disableEnforceFocus
                hideBackdrop
                BackdropProps={{ open: false }}
                fullWidth
                maxWidth="sm"  // Constrain width
            >
                <DialogTitle>Mast Setting</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {/* Name and Mounting - stacked */}
                        <Grid item xs={12}>
                            <TextField
                                label="Mast Name"
                                name="name"
                                value={currColumn?.name || ''}
                                onChange={handleChangeCurrColumn}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Mounting"
                                name="mounting"
                                value={currColumn?.values?.mountingType || 'column'}
                                onChange={(e) =>
                                    handleChangeCurrColumn({
                                        target: { name: 'mounting', value: e.target.value },
                                    })
                                }
                                fullWidth
                                variant="outlined"
                            >
                                <MenuItem value="column">Mast</MenuItem>
                                <MenuItem disabled value="wall">Wall (In Development)</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Lantern Count and Buttons */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Number of Lanterns"
                                name="lanternCount"
                                variant="outlined"
                                value={currColumn?.values?.lanternCount || 1}
                                onChange={(e) =>
                                    handleChangeCurrColumn({
                                        target: { name: 'lanternCount', value: e.target.value },
                                    })
                                }
                            >
                                {Array.from({ length: 16 }, (_, i) => i + 1).map((count) => (
                                    <MenuItem key={count} value={count}>
                                        {count}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            <Button
                                onClick={() => toggleSetLanternsDialog(true)}
                                variant="outlined"
                                sx={{ height: '56px', minWidth: '100px' }}
                            >
                                Set Lanterns
                            </Button>
                            <Button
                                onClick={() => handleAddLantern(null)}
                                variant="contained"
                                disabled={currColumn?.values?.lanterns?.length >= 16}
                                sx={{ height: '56px', minWidth: '80px' }}
                            >
                                Add
                            </Button>
                        </Grid>

                        <Grid item xs={12}>
                            <InputLabel sx={{ mb: 1 }}>Lanterns</InputLabel>
                            <Box
                                sx={{
                                    height: 160,
                                    overflowY: 'auto',
                                    pr: 1,
                                    pt: 1,
                                }}
                            >
                                {currColumn?.values?.lanterns?.map((lantern, index) => {
                                    const showOutreach = currColumn?.values?.useLanternOutreach;
                                    const showTilt = currColumn?.values?.configuration !== "None";
                                    const mountingType = currColumn?.values?.mountingType || 'column';

                                    const visibleFields = (mountingType == "column" ? 2 : 3) + (showOutreach ? 1 : 0) + (showTilt ? 1 : 0);
                                    const gridSize = Math.floor(12 / (visibleFields + 1));

                                    return (
                                        <Grid
                                            container
                                            spacing={1}
                                            key={lantern.id || index}
                                            alignItems="center"
                                            sx={{ mb: 1 }}
                                        >
                                            {currColumn?.values?.mountingType === 'column' ? (
                                                <Grid item xs={gridSize}>
                                                    <TextField
                                                        label={`Lantern ${index + 1} Angle`}
                                                        value={storedToUiAngle(lantern.positionAngle ?? 0)}
                                                        onChange={(e) => handleLanternAngleChange(index, e.target.value)}
                                                        type="number"
                                                        variant="outlined"
                                                        fullWidth
                                                    />
                                                </Grid>
                                            ) : (
                                                <>
                                                    <Grid item xs={gridSize}>
                                                        <TextField
                                                            label={`Lantern ${index + 1} Angle`}
                                                            value={storedToUiAngle(lantern.positionAngle ?? 0)}
                                                            onChange={(e) => handleLanternAngleChange(index, e.target.value)}
                                                            type="number"
                                                            variant="outlined"
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={gridSize}>
                                                        <TextField
                                                            label={`Lantern ${index + 1} Offset`}
                                                            value={lantern.offset}
                                                            onChange={(e) => handleLanternOffsetChange(index, e.target.value)}
                                                            type="number"
                                                            variant="outlined"
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                </>
                                            )}


                                            {/* Luminaire Select */}
                                            <Grid item xs={gridSize}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel id={`luminaire-label-${index}`}>Luminaire</InputLabel>
                                                    <Select
                                                        labelId={`luminaire-label-${index}`}
                                                        label="Luminaire"
                                                        value={lantern.luminaireId || luminIdList[0]}
                                                        onChange={(e) => handleLanternLuminaireChange(index, e.target.value)}
                                                        variant="outlined"
                                                        name="luminaireId"
                                                        style={{
                                                            color: luminColorList[
                                                                luminIdList.indexOf(lantern.luminaireId || luminIdList[0])
                                                            ]
                                                        }}
                                                    >
                                                        {luminNameList.map((option, id) => (
                                                            <MenuItem
                                                                key={option}
                                                                value={luminIdList[id]}
                                                                style={{ color: luminColorList[id] }}
                                                            >
                                                                {option}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>


                                            {/* Outreach (if enabled) */}
                                            {showOutreach && (
                                                <Grid item xs={gridSize}>
                                                    <TextField
                                                        label="Outreach"
                                                        value={lantern.outreach ?? ''}
                                                        onChange={(e) => handleLanternOutreachChange(index, e.target.value)}
                                                        type="number"
                                                        variant="outlined"
                                                        fullWidth
                                                    />
                                                </Grid>
                                            )}

                                            {/* Tilt (if config !== None) */}
                                            {showTilt && (
                                                <Grid item xs={gridSize}>
                                                    <TextField
                                                        label="Tilt"
                                                        value={lantern.beamAimingInfo.tilt ?? ''}
                                                        onChange={(e) => handleLanternTiltChange(index, e.target.value)}
                                                        type="number"
                                                        variant="outlined"
                                                        fullWidth
                                                    />
                                                </Grid>
                                            )}

                                            {/* Remove Button */}
                                            <Grid item xs={gridSize}>
                                                {index > 0 && (
                                                    <IconButton
                                                        onClick={() => handleRemoveLantern(index)}
                                                        variant="outlined"
                                                        fullWidth
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </Grid>
                                    );
                                })}

                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Configuration"
                                name="configuration"
                                value={currColumn?.values?.configuration || 'None'}
                                onChange={handleChangeCurrColumn}
                                variant="outlined"
                            >
                                <MenuItem value="None">None</MenuItem>
                                <MenuItem value="Beam Aiming">Beam Aiming</MenuItem>
                                <MenuItem value="Asymmetrical Flood Aiming">Asymmetrical Flood Aiming</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <InputLabel sx={{ mb: 1 }}>Mast Configuration</InputLabel>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="x"
                                    value={displayX || ''}
                                    onChange={handleChangeCurrColumn}
                                    variant="outlined"
                                    type="number"
                                    name="x"
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="y"
                                    value={displayY || ''}
                                    onChange={handleChangeCurrColumn}
                                    variant="outlined"
                                    type="number"
                                    name="y"
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Angle"
                                    value={displayAngle ?? 0}
                                    onChange={handleChangeCurrColumn}
                                    variant="outlined"
                                    type="number"
                                    name="angle"
                                />
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider style={{ margin: '10px' }} />
                        </Grid>

                        {['height', 'outreach', 'tilt', 'cant'].map((field) => (
                            <Grid item xs={12} sm={6} key={field}>
                                {field === "outreach" ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                        {!currColumn.values.useLanternOutreach ? (
                                            <TextField
                                                fullWidth
                                                label={field.charAt(0).toUpperCase() + field.slice(1)}
                                                value={currColumn?.values?.[field] ?? ''}
                                                onChange={handleChangeCurrColumn}
                                                variant="outlined"
                                                type="number"
                                                name={field}
                                            />
                                        ) : (
                                            <Box sx={{ height: '56px', width: '100%' }} />
                                        )}

                                        <label style={{ display: "flex", alignItems: "center", gap: "5px", cursor: "pointer" }}>
                                            <span>Individual</span>
                                            <Switch
                                                onChange={handleLanternOutreach}
                                                checked={currColumn.values.useLanternOutreach || false}
                                                name="useLanternOutreach"
                                                color="primary"
                                            />
                                        </label>
                                    </div>
                                ) : field === "tilt" ? (
                                    currColumn.values.configuration === "None" ? (
                                        <TextField
                                            fullWidth
                                            label={field.charAt(0).toUpperCase() + field.slice(1)}
                                            value={currColumn?.values?.[field] ?? ''}
                                            onChange={handleChangeCurrColumn}
                                            variant="outlined"
                                            type="number"
                                            name={field}
                                        />
                                    ) : (
                                        <Box sx={{ height: '56px', width: '100%' }} />
                                    )
                                ) : (
                                    <TextField
                                        fullWidth
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={currColumn?.values?.[field] ?? ''}
                                        onChange={handleChangeCurrColumn}
                                        variant="outlined"
                                        type="number"
                                        name={field}
                                    />
                                )}
                            </Grid>
                        ))}

                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCenterMarkOnCurrentColumn} variant="outlined">
                        Center Mark
                    </Button>
                    <Button onClick={handleSaveMasts} variant="contained" color="primary">
                        Save
                    </Button>
                    <Button onClick={() => setMastSettingsOpen(false)} variant="contained">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog >

            <Dialog open={setLanternsDialogOpen} onClose={() => toggleSetLanternsDialog(false)}>
                <DialogTitle>Set Lanterns?</DialogTitle>
                <DialogContent>
                    This will set all lanterns to a default configuration. Do you want to continue? - You will lose any custom lantern settings.
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            handleAddLantern(parseInt(currColumn?.values?.lanternCount || 1));
                            toggleSetLanternsDialog(false);
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                    <Button onClick={() => toggleSetLanternsDialog(false)} color="inherit" variant="contained">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>


            {editGroupDialog()}

            <Dialog
                open={groupSettingsOpen}
                onClose={() => {
                    setGroupSettingsOpen(false);
                    setGroupHasColumns(false);
                }}
                disableEscapeKeyDown
                disableEnforceFocus
                hideBackdrop
                BackdropProps={{ open: false }}
            >
                <DialogTitle>Group Setting: {groupName}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                        {groupHasColumns
                            ? "Tick the checkbox next to each field to apply its value to all columns in this group."
                            : "This group is empty right now. You can still edit the group here."}
                    </Typography>
                    <TextField
                        label="Group Name"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        style={{ marginTop: '10px', width: '100%' }}
                        variant="outlined"
                        disabled={groupId === 0}
                    />
                </DialogContent>
                {groupHasColumns && <DialogContent>
                    <Tooltip title="Select all" placement="top">
                        <IconButton
                            edge="end"
                            aria-label="group-select"
                            onClick={() => handleGroupSettingSelectAll()}
                            style={{ marginLeft: 'auto', marginBottom: '10px' }}
                        >
                            <DoneAllIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Unselect all" placement="top">
                        <IconButton
                            edge="end"
                            aria-label="group-select"
                            onClick={() => handleGroupSettingUnselectAll()}
                            style={{ marginLeft: 'auto', marginBottom: '10px' }}
                        >
                            <ClearAllIcon />
                        </IconButton>
                    </Tooltip>
                </DialogContent>}
                {groupHasColumns && <DialogContent>
                    {['height', 'outreach'].map((field) => (
                        <>
                            <TextField
                                label={field.charAt(0).toUpperCase() + field.slice(1) + ' (m)'}
                                value={currColumn?.values?.[field] ?? 0}
                                onChange={(e) => handleChangeCurrColumn(e)}
                                style={{ margin: '10px' }}
                                variant="outlined"
                                type="number"
                                name={field}
                            />
                            <Checkbox
                                checked={groupSettingOptions[field]}
                                onChange={(e) => setGroupSettingOptions({
                                    ...groupSettingOptions,
                                    [field]: e.target.checked
                                })}
                                color="primary"
                            />
                        </>
                    ))}
                    {['tilt', 'cant', 'positionAngle'].map((field) => (
                        <>
                            <TextField
                                label={field == 'positionAngle' ? 'Angle' : field.charAt(0).toUpperCase() + field.slice(1) + ' (°)'}
                                value={field === 'positionAngle' ? (displayAngle ?? 0) : (currColumn?.values?.[field] ?? 0)}
                                onChange={(e) => handleChangeCurrColumn(e)}
                                style={{ margin: '10px' }}
                                variant="outlined"
                                type="number"
                                name={field}
                            />
                            <Checkbox
                                checked={groupSettingOptions[field]}
                                onChange={(e) => setGroupSettingOptions({
                                    ...groupSettingOptions,
                                    [field]: e.target.checked
                                })}
                                color="primary"
                            />
                        </>
                    ))}
                    <InputLabel id="luminaireId">Column Type</InputLabel>
                    {luminNameList?.length > 0 && luminIdList.length > 0 ? (
                        <>
                            <Select
                                value={currColumn?.values?.luminaireId || luminIdList[0]}
                                onChange={(e) => handleChangeCurrColumn(e)}
                                style={{
                                    margin: '10px',
                                    width: '80%',
                                    color: luminColorList[luminIdList.indexOf(currColumn?.values?.luminaireId || luminIdList[0])]
                                }}
                                variant="outlined"
                                name="luminaireId"
                                label="luminaireId"
                            >
                                {luminNameList.map((option, id) => (
                                    <MenuItem
                                        key={option}
                                        value={luminIdList[id]}
                                        style={{ color: luminColorList[id] }}
                                    >
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Checkbox
                                checked={groupSettingOptions.luminaireId}
                                onChange={(e) => setGroupSettingOptions({
                                    ...groupSettingOptions,
                                    luminaireId: e.target.checked
                                })}
                                color="primary"
                            />
                        </>
                    ) : (
                        <Typography>Please Add Column Type(s)</Typography>
                    )}
                </DialogContent>}
                <div style={{ textAlign: "right", margin: 10 }}>
                    <Button
                        onClick={() => {
                            handleGroupSettingSave();
                            setGroupHasColumns(false);
                        }}
                        color="primary"
                        style={{ margin: '10px' }}
                        variant="contained"
                    >
                        <Typography>Save</Typography>
                    </Button>
                    <Button
                        onClick={() => {
                            setGroupSettingsOpen(false);
                            setGroupHasColumns(false);
                        }}
                        color="primary"
                        style={{ margin: '10px' }}
                        variant="contained"
                    >
                        <Typography>Cancel</Typography>
                    </Button>
                </div>
            </Dialog>
            <Button
                onClick={handleGroupDialogOpen}
                color="primary"
                variant="contained"
            >
                Edit Groups
            </Button>
            <Box sx={{ maxHeight: "500px", overflowY: "auto", width: "100%" }}>
                {(Array.isArray(props.columnGroups) ? props.columnGroups : []).map((group) => (
                    <Accordion
                        key={group.name}
                        expanded={Boolean(expandedGroups[group.id])}
                        onChange={() => handleToggleGroupExpanded(group.id)}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, gap: 8 }}>
                                <Tooltip title={group.name} placement="top">
                                    <Typography style={{
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem',
                                        marginTop: 'auto',
                                        marginBottom: 'auto',
                                        marginLeft: '10px',
                                        minWidth: 0,
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>{group.name}</Typography>
                                </Tooltip>
                                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: 'auto' }}>
                                    <IconButton
                                        edge="end"
                                        aria-label="group-settings"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupSettings(group.id);
                                        }}
                                    >
                                        <SettingsIcon />
                                    </IconButton>
                                    <Tooltip title={group.locked ? "Unlock group" : "Lock group"} placement="top">
                                        <IconButton
                                            edge="end"
                                            aria-label="group-lock"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleGroupLock(group.id);
                                            }}
                                            color={"default"}
                                        >
                                            {group.locked ? <LockIcon /> : <LockOpenIcon />}
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton
                                        edge="end"
                                        aria-label="group-duplicate"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupDuplicate(group.id);
                                        }}
                                    >
                                        <FileCopyIcon />
                                    </IconButton>
                                    {group.id !== 0 && (
                                        <Tooltip title="Move column to default group and delete this group" placement="top">
                                            <IconButton
                                                edge="end"
                                                aria-label="ungroup"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleUngroup(group.id)
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </AccordionSummary>
                        <Tooltip title="Select all" placement="top">
                            <IconButton
                                edge="end"
                                aria-label="group-select"
                                onClick={() => handleGroupSelect(group.id)}
                                style={{ marginLeft: 'auto' }}
                            >
                                <SelectAllIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Sync" placement="top">
                            <IconButton
                                edge="end"
                                aria-label="sync-group-move"
                                onClick={() => handleGroupSync(group.id)}
                                style={{ marginLeft: 'auto' }}
                            >
                                {group.sync
                                    ? <SyncIcon style={{ fontSize: 24 }} />
                                    : <SyncDisabledIcon style={{ fontSize: 24 }} />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Flip Horizontally" placement="top">
                            <IconButton
                                edge="end"
                                aria-label="Flip Horizontally"
                                onClick={() => handleGroupFlipHorizontally(group.id)}
                                style={{ marginLeft: 'auto' }}
                            >
                                <FlipIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Flip Vertically" placement="top">
                            <IconButton
                                edge="end"
                                aria-label="Flip Vertically"
                                onClick={() => handleGroupFlipVertically(group.id)}
                                style={{ marginLeft: 'auto', transform: 'rotate(90deg)' }}
                            >
                                <FlipIcon />
                            </IconButton>
                        </Tooltip>
                        <AccordionDetails>
                            <List>
                                {props.columns.filter(col => group?.columnIds?.includes(col.id))
                                    .map((col, index) =>
                                        <ListItem
                                            key={index}
                                            onClick={() => {
                                                if (!isGroupLocked(group.id)) {
                                                    props.selectColumn(col.id);
                                                }
                                            }}
                                            style={{
                                                ...(col.selected ? { border: "2px solid darkturquoise " } : {}),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '8px',
                                            }}
                                        >
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <Tooltip title={col.name} placement="top">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                        {col.isMast ? (
                                                            <WorkspacesIcon sx={{ fontSize: 20 }} />
                                                        ) : (
                                                            <CircleIcon sx={{ color: columnColors[col.id], fontSize: 20 }} />
                                                        )}
                                                        <ListItemText
                                                            primary={col.name}
                                                            primaryTypographyProps={{
                                                                style: {
                                                                    userSelect: 'none',
                                                                    color: col.isMast ? "" : columnColors[col.id],
                                                                    fontSize: 14,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                <IconButton edge="end" aria-label="setting" onClick={(e) => { e.stopPropagation(); handleInfoOpen(col.id); }}>
                                                    <SettingsIcon />
                                                </IconButton>
                                                <Tooltip title={col.isMast ? "Swap to Column" : "Swap to Mast"} placement="top">
                                                    <IconButton edge="end" aria-label="setting" onClick={(e) => { e.stopPropagation(); swapType(col.id); }}>
                                                        <SwapHoriz />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Focus on column" placement="top">
                                                    <IconButton edge="end" aria-label="setting" onClick={(e) => { e.stopPropagation(); props.focusColumn(col.id); }}>
                                                        <CenterFocusWeakIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); props.deleteColumn(col.id); }}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </div>
                                        </ListItem>
                                    )}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </>
    );
}

